<script setup lang="ts">
    import giftcardLogo from '@/assets/images/constell-giftcard.png';
    import hotelPmsLogo from '@/assets/images/constell-hotel-pms.png';
    import tableReservationLogo from '@/assets/images/constell-tablereservation.png';
    import { openWidgetPartReservation } from '@/helpers/reservation-openers';
    import { getContrastColor } from '@/helpers/text';
    import type { PartnerCategory, WidgetPartTyping } from '@/typings/widget';

    const props = defineProps<{
        categories: PartnerCategory[]
        brandColor?: string

        // Desktop only: labels start open and never animate. Tablet/mobile keep hover/focus expand.
        expanded?: boolean
    }>();

    const emit = defineEmits<{(event: 'expandedChange', expanded: boolean)}>();

    // Stores
    const { venueWidgetParts } = storeToRefs(useWidgetVenueStore());
    const trackingStore = useWidgetTrackingStore();

    // Constants
    const PARTNER_SLOT_TYPE = 'L';

    // Mirrors the `lg` key of $breakpoints (shared/_mixins/breakpoints) - the desktop edge.
    const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

    interface CategorySlot {
        partnerIds: number[]
        logo: string
    }

    interface ActiveButton extends CategorySlot {
        category: PartnerCategory
        widgetPart: WidgetPartTyping
        label: string
    }

    /*
     * Keyed on partnerId, matching providerOpeners in reservation-openers.ts - keep the two in
     * step, so we never render a button we cannot open. One Constell logo per category, not the
     * partner's own brand logo. Not keyed on widgetPartName (localized) or partnerName (free
     * text, inconsistent casing).
     */
    const CATEGORY_SLOTS: Record<PartnerCategory, CategorySlot> = {
        table: {
            partnerIds: [
                1, 2, 3,
            ],
            logo: tableReservationLogo,
        },

        // 5 is presumed RoomRaccoon: VMA shows a URL field for the hotel slot + partner 5.
        hotel: {
            partnerIds: [4, 5],
            logo: hotelPmsLogo,
        },
        gift: {
            partnerIds: [6],
            logo: giftcardLogo,
        },
    };

    // Type 'L' first so internal parts and custom links can never collide.
    const matchesSlot = (part: WidgetPartTyping, slot: CategorySlot): boolean => part.widgetPartType === PARTNER_SLOT_TYPE
        && slot.partnerIds.includes(Number(part.partnerId));

    // Composables
    const isDesktop: Ref<boolean> = useMediaQuery(DESKTOP_MEDIA_QUERY);

    // Refs
    const hovered: Ref<boolean> = ref(false);
    const keyboardFocused: Ref<boolean> = ref(false);

    // Computed
    // Contrast tone for divider + hover, adapting to the brand-coloured background:
    // light brand -> dark tone, dark brand -> light tone.
    const contrastColor: ComputedRef<string> = computed(() => getContrastColor(props.brandColor ?? '#282829'));

    // Iterating the enabled categories is the filter; order follows App, not attribute order.
    const activeButtons: ComputedRef<ActiveButton[]> = computed(() => props.categories.flatMap(category => {
        const slot = CATEGORY_SLOTS[category];
        const widgetPart = venueWidgetParts.value.find(part => part.activated && matchesSlot(part, slot));
        if (!widgetPart) return [];

        /*
         * The venue names the slot in VMA; fall back to the partner filling it. `||`, not `??`:
         * an unnamed slot comes back as '' rather than null, which would otherwise render a
         * button with no label and an empty title.
         */
        const label = widgetPart.widgetPartName || widgetPart.name || widgetPart.partnerName || '';
        return [
            {
                ...slot,
                category,
                widgetPart,
                label,
            },
        ];
    }));

    // Truthy, not strict: the widget attribute arrives bare (cast to true) or as a string "1".
    const alwaysExpanded: ComputedRef<boolean> = computed(() => Boolean(props.expanded) && isDesktop.value);

    /*
     * Either input keeps the launcher collapsed, so it can never un-collapse under a label
     * that is still expanded. Always-expanded labels never change width, so there is nothing
     * for the launcher to make room for - it stays full width instead of shrinking on hover.
     */
    const isExpanded: ComputedRef<boolean> = computed(
        () => !alwaysExpanded.value && (hovered.value || keyboardFocused.value),
    );

    // Methods
    /*
     * Bound to the row, not the items: mouseenter/mouseleave don't bubble (so no flicker moving
     * between logos) while focusin/focusout do (one pair covers every button). Hover and focus
     * are tracked apart because focus outlives the pointer - tab to a button, then move the
     * mouse over the row and off again, and the label is still expanded by :focus-visible, so
     * the launcher has to stay collapsed with it. Only keyboard focus counts, mirroring the
     * :focus-visible rule below: a mouse click focuses the button without expanding its label.
     */
    const handleFocusIn = (event: FocusEvent): void => {
        keyboardFocused.value = (event.target as HTMLElement).matches(':focus-visible');
    };

    const handleClick = (button: ActiveButton): void => {
        // The partner, not the slot label - otherwise Zenchef and Guestplan clicks look alike.
        trackingStore.trackPartnerClicked({
            partnerId: Number(button.widgetPart.partnerId),
            partnerName: button.widgetPart.partnerName ?? button.label,
        });
        openWidgetPartReservation(button.widgetPart);
    };

    // Watchers
    watch(() => isExpanded.value, (nVal: boolean) => {
        emit('expandedChange', nVal);
    });

    /*
     * An uncategorised partner means CATEGORY_SLOTS is out of date. Dev server only: this is a
     * signal for us, and the widget logs into the customer's console on their own site.
     * `import.meta.hot` rather than `import.meta.env.DEV` - see the note in main.ts, deployed
     * dev/stage builds report DEV truthy.
     */
    if (import.meta.hot) {
        watchEffect(() => {
            const unknown = venueWidgetParts.value.filter(part => part.activated
                && part.widgetPartType === PARTNER_SLOT_TYPE
                && !Object.values(CATEGORY_SLOTS)
                    .some(slot => matchesSlot(part, slot)));

            if (unknown.length) {
                // eslint-disable-next-line no-console
                console.warn(
                    '[constell-widget] unrecognised partner slot(s), no button rendered:',
                    unknown.map(part => ({
                        widgetPartId: part.widgetPartId,
                        partnerName: part.partnerName,
                    })),
                );
            }
        });
    }
</script>

<template>
    <div
        v-if="activeButtons.length"
        class="partner-buttons"
        :class="{ 'partner-buttons--expanded': alwaysExpanded }"
        :style="{ '--partner-contrast': contrastColor }"
        @mouseenter="hovered = true"
        @mouseleave="hovered = false"
        @focusin="handleFocusIn"
        @focusout="keyboardFocused = false"
    >
        <button
            v-for="button in activeButtons"
            :key="button.category"
            type="button"
            class="partner-buttons__item"
            :title="button.label"
            @click="handleClick(button)"
        >
            <div class="partner-buttons__item-content">
                <!-- Decorative: the label span next to it already names the button. -->
                <img
                    :src="button.logo"
                    alt=""
                    class="partner-buttons__icon"
                >
                <span class="partner-buttons__label">{{ button.label }}</span>
            </div>
        </button>
    </div>
</template>

<style scoped lang="scss">
.partner-buttons {
    display: flex;
    flex-direction: row;
    align-items: center;

    // Lets inline-size animate between 0 and auto; unsupported browsers snap instead.
    interpolate-size: allow-keywords;

    &__item {
        padding-block: 0;
        padding-inline: 2px;

        &-content {
            display: flex;
            flex-shrink: 0;
            align-items: center;
            border: none;
            border-radius: 5px;
            background: var(--widget-brand-color);
            block-size: var(--spacing-08);

            // 36px icon + 10px per side = 56px collapsed square (--spacing-08),
            // and the same inline padding holds in the expanded state.
            padding-inline: 10px;
            transition: background-color 0.2s ease;
        }

        &:hover .partner-buttons__item-content {
            background-color: color-mix(in srgb, var(--partner-contrast) 15%, var(--widget-brand-color));
        }
    }

    &__icon {
        display: block;
        flex-shrink: 0;
        border-radius: var(--border-radius-sm);
        block-size: var(--size-6);
        inline-size: var(--size-6);
        object-fit: contain;
    }

    &__label {
        display: inline-block;
        overflow: hidden;
        color: var(--partner-contrast);
        font: 16px var(--font-primary);
        inline-size: 0;
        margin-inline-start: 0;
        opacity: 0;
        transition:
            inline-size 0.3s ease 0.15s,
            margin-inline-start 0.3s ease 0.15s;
        white-space: nowrap;
    }

    &__item:hover &__label,
    &__item:focus-visible &__label {
        inline-size: auto;
        margin-inline-start: var(--spacing-base);
        opacity: 1;
        transition-delay: 0.1s;
    }

    // `expanded` attribute, desktop only (the class is only set above the lg breakpoint):
    // labels are open from the first paint, so nothing animates on hover or focus.
    &--expanded &__label {
        inline-size: auto;
        margin-inline-start: var(--spacing-base);
        opacity: 1;
        transition: none;
    }
}
</style>
